import { Component, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter } from 'rxjs';
import { FooterComponent } from '../../components/general/footer/footer.component';
import { LayoutHeaderComponent } from '../../components/general/layout-header/layout-header.component';
import { AppService } from 'briteatlib';
import { SharedService } from '../../services/shared.service';


@Component({
  selector: 'brt-main-layout',
  standalone: true,
  imports: [
    RouterOutlet,
    LayoutHeaderComponent,
    FooterComponent,
  ],
  templateUrl: './main-layout.component.html',
  styleUrl: './main-layout.component.scss'
})
export class MainLayoutComponent {
  business: any;
  hq: any;
  bannerSrc = signal<string | null>(null);
  defaultBanner: string = 'assets/images/gallery/banner-default.webp';
  routeFullWidth = signal(false);
  // Full-width (no banner / container) only on the BritEat platform landing page,
  // so business and HQ home pages keep their banner and centred layout.
  fullWidth = signal(false);
  // The business home page renders its own hero (with the same background image),
  // so the layout banner there is a duplicate header and must be hidden.
  hideBanner = signal(false);

  constructor(private sharedService: SharedService, private appService: AppService,
    private router: Router, private route: ActivatedRoute) {
    this.appService.activeBusiness$.pipe(takeUntilDestroyed()).subscribe(business => {
      this.business = business;
      this.updateBanner();
      this.updateFullWidth();
      this.applyTheme();
    });
    this.appService.activeHq$.pipe(takeUntilDestroyed()).subscribe(hq => {
      this.hq = hq;
      this.updateBanner();
      this.updateFullWidth();
    });
    this.router.events.pipe(filter(e => e instanceof NavigationEnd), takeUntilDestroyed())
      .subscribe(() => this.readRouteData());
    this.readRouteData();
  }

  async ngOnInit() {
    await this.sharedService.setActiveBusiness();
    this.updateBanner();
  }

  private readRouteData() {
    let child = this.route.firstChild;
    let fullWidth = false;
    let hideBanner = false;
    while (child) {
      if (child.snapshot?.data?.['fullWidth']) fullWidth = true;
      if (child.snapshot?.data?.['hideBanner']) hideBanner = true;
      child = child.firstChild;
    }
    this.routeFullWidth.set(fullWidth);
    this.hideBanner.set(hideBanner);
    this.updateFullWidth();
  }

  private updateFullWidth() {
    this.fullWidth.set(this.routeFullWidth() && !this.business && !this.hq);
  }

  private updateBanner() {
    const businessBanner = this.business?.UI?.['BackgroundImage'];
    const hqBanner = this.hq?.UI?.['BackgroundImage'];
    this.bannerSrc.set(businessBanner || hqBanner || this.defaultBanner);
  }

  onBannerError() {
    // If the business banner fails to load, fall back to the default.
    if (this.bannerSrc && this.bannerSrc() !== this.defaultBanner) {
      this.bannerSrc.set(this.defaultBanner);
    }
  }

  // Apply the active business's brand colour to the whole site so each restaurant's
  // page (buttons, accents, hero CTA) is themed to match its identity. The value
  // comes from the per-business UI.ThemeColor setting and falls back to the
  // platform default when unset.
  private applyTheme() {
    if (!this.appService.isBrowser) return;
    const root = document.documentElement;
    const color = this.business?.UI?.['ThemeColor'];
    if (color && /^#?[0-9a-fA-F]{6}$/.test(color.trim())) {
      const hex = color.trim().startsWith('#') ? color.trim() : `#${color.trim()}`;
      root.style.setProperty('--brand-color-main', hex);
      root.style.setProperty('--brand-color-main-darker', this.shade(hex, -0.18));
      root.style.setProperty('--brand-color-main-lighter', this.shade(hex, 0.22));
    } else {
      root.style.removeProperty('--brand-color-main');
      root.style.removeProperty('--brand-color-main-darker');
      root.style.removeProperty('--brand-color-main-lighter');
    }
  }

  // Lighten (ratio > 0) or darken (ratio < 0) a #rrggbb colour by the given ratio.
  private shade(hex: string, ratio: number): string {
    const n = parseInt(hex.slice(1), 16);
    const adjust = (c: number) => Math.max(0, Math.min(255, Math.round(c + (ratio < 0 ? c : 255 - c) * ratio)));
    const r = adjust((n >> 16) & 0xff);
    const g = adjust((n >> 8) & 0xff);
    const b = adjust(n & 0xff);
    return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
  }
}
